'use client';

import { normalizeProductQuantity } from '@eliadmin/core/src/utils/product-utils';
import type { CartItemPackages } from '@eliadmin/core/src/api/cart/cart-types';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCurrencies } from '@eliadmin/core/src/api/general/general-api';
import type {
  ProductDetailData,
  ProductDisplayData,
} from '@eliadmin/core/src/api/products/types/product-types';
import { fetchSlugDetail } from '@eliadmin/core/src/api/slugs/slugs-api';
import { trackEcommerce } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import { AddToCartDialog } from '@eliadmin/core/src/components/dialog/lazy-dialogs';
import { AddToCartVariantDialog } from '@eliadmin/core/src/components/dialog/lazy-dialogs';
import type { ButtonProps } from '@eliadmin/core/src/components/ui/button';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { useAddToCart } from '@eliadmin/core/src/hooks/cart/use-add-to-cart';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import type { SelectedServices } from '@eliadmin/core/src/stores/variant-store';
import { VariantsProvider, useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FaCartShopping } from 'react-icons/fa6';

type AddToCartButtonProps = ButtonProps & {
  product: ProductDisplayData;
  quantity?: number;
  className?: string;
  showIcon?: boolean;
  sellable?: boolean;
  customText?: string;
  type?: string;
  customValuesError?: boolean;
  variantId?: number;
};
export function AddToCartButton({
  className,
  showIcon,
  sellable,
  product,
  customText,
  quantity = 1,
  customValuesError = false,
  variantId,
  ...props
}: AddToCartButtonProps) {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const openDialog = useDialogStore(s => s.open);
  const { me, isInitialized } = useCustomerStore();

  const addToCartMutation = useAddToCart();

  const globalSelectedVariant = useVariantsStore(state => state.selectedVariant);
  // Use variant from variantId if provided, otherwise use selectedVariant from store
  const selectedVariant = variantId
    ? product.variants?.find(v => v.id === variantId)
    : globalSelectedVariant;
  const selectedServices = useVariantsStore(state => state.selectedServices);
  const selectedPackages = useVariantsStore(state => state.selectedPackages);
  const selectedValues = useVariantsStore(state => state.selectedValues);
  const globalSelectedQuantity = useVariantsStore(state => state.selectedQuantity);
  // Use quantity prop if provided, otherwise use selectedQuantity from store
  // For variant-specific quantities, the quantity prop will contain the variant-specific value
  const selectedQuantity = quantity !== undefined ? quantity : globalSelectedQuantity;
  const customValues = useVariantsStore(state => state.customValues);
  const priceDisplay = getProductPriceDisplay(selectedVariant ?? product, {
    isWholesaler: me?.customer.is_wholesaler,
  });
  const hasUnresolvedVariants =
    (!!product.has_variants || !!product.variants?.length) && !selectedVariant;
  const priceOnRequest = !hasUnresolvedVariants && priceDisplay.priceOnRequest;

  const productQuery = useQuery({
    queryKey: [CacheKey.PRODUCTS, 'detail', locale, product.id, me?.authToken ?? null],
    queryFn: () =>
      fetchSlugDetail({ slug: product.slug, locale, authToken: me?.authToken })
        .then(res => res['App\\Models\\Product'])
        .catch(() => null),
    enabled: false,
  });

  const sortedSuppliers = [...(product.suppliers ?? [])].sort((a, b) => a.position - b.position);

  const currencySymbol = useGlobalStore.getState().generalLayout.currency;
  const [currencyISO, setCurrencyISO] = useState<string>('EUR');

  const { data: currencies } = useQuery({
    queryKey: [CacheKey.CURRENCIES],
    queryFn: () => fetchCurrencies({ locale }),
  });

  useEffect(() => {
    if (currencies) {
      setCurrencyISO(
        currencies?.data.find(currency => currency.symbol === currencySymbol)?.id || currencySymbol,
      );
    }
  }, [currencies, currencySymbol]);

  const firstSupplier = sortedSuppliers[0];
  const isProductEnded = product.status === 'ended';

  const handleAddToCart = async () => {
    if (!isInitialized || priceOnRequest || (!sellable && quantity < 1)) return;

    const fullProduct = product.has_variants
      ? await productQuery.refetch().then(res => res.data)
      : product;

    if (fullProduct?.variants?.length) {
      const providerKey = fullProduct.id
        ? `product-${fullProduct.id}`
        : `product-${fullProduct.slug}`;

      openDialog({
        id: AddToCartVariantDialog.id,
        content: (
          <VariantsProvider
            key={providerKey}
            state={{
              product: fullProduct,
              selectedPackages,
              selectedVariant,
              selectedQuantity,
              selectedServices,
              selectedValues,
              customValues,
              customValuesError,
            }}
          >
            <AddToCartVariantDialog
              product={fullProduct}
              quantity={normalizeProductQuantity(selectedQuantity || 1, fullProduct)}
            />
          </VariantsProvider>
        ),
      });
    } else {
      await addToCartMutation.mutateAsync({
        locale,
        // TODO: implement quantity
        body: {
          item: {
            product: { id: product.id },
            sub_items: {
              services: selectedServices
                ? selectedServices.map((selectedService: SelectedServices) => {
                    return {
                      id: selectedService.id,
                    };
                  })
                : [],
              packages: selectedPackages
                ? selectedPackages.map(selectedPackage => {
                    return {
                      id: selectedPackage.id,
                    } as CartItemPackages;
                  })
                : [],
            },
          },
          qty: normalizeProductQuantity(quantity, product),
        },
      });
      trackEcommerce.addToCart({
        id: product.id.toString(),
        name: product.name,
        price: priceDisplay.currentWithVat ?? 0,
        currency: currencyISO,
      });
      openDialog({
        id: AddToCartDialog.id,
        content: <AddToCartDialog product={product} title="text631" />,
      });
    }
  };

  if (!firstSupplier?.is_affiliate && priceOnRequest) return null;

  return (
    <>
      {!firstSupplier?.is_affiliate && (
        <Button
          className={cn('space-x-1', className)}
          onClick={e => {
            e.stopPropagation();
            e.nativeEvent.preventDefault();
            handleAddToCart();
          }}
          disabled={
            !isInitialized || priceOnRequest || !sellable || isProductEnded || customValuesError
          }
          variant={'default'}
          isLoading={!isInitialized || addToCartMutation.isPending || productQuery.isFetching}
          aria-label={customText ? t(customText) : Array.from(new Set([t('text1115'), t('text227')])).join(' ')}
          {...props}
        >
          {showIcon && <FaCartShopping className="text-white" size={20} />}
          <span className={cn('line-clamp-1 inline text-ellipsis @[200px]:hidden')}>
            {customText ? t(customText) : t('text1115')}
          </span>
          <span className={cn('line-clamp-1 hidden text-ellipsis @[200px]:inline')}>
            {customText ? t(customText) : t('text227')}
          </span>
        </Button>
      )}
      {firstSupplier?.is_affiliate && (
        <Button
          className={cn('space-x-1', className)}
          asChild
          variant={'default'}
          isLoading={addToCartMutation.isPending || productQuery.isFetching}
          aria-label={customText ? t(customText) : t('text1226')}
          {...props}
        >
          <a href={firstSupplier.url ?? '#'}>
            {showIcon && <FaCartShopping className="text-white" size={20} />}
            <span className={cn('line-clamp-1 inline text-ellipsis text-center')}>
              {customText ? t(customText) : t('text1226')}
            </span>
          </a>
        </Button>
      )}
    </>
  );
}

// /**
//  * Deprecated and empty not to break other eshops
//  * @deprecated
//  */
// function addProductToAnalytics(product: ProductDetailData | Product, quantity: number = 1) {
//   //
// }
